import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

// Back Office closes an open claim herself (new logo mailed / resolved) — no factory alert.
export function SelfResolveDialog({ claim, action, onClose }) {
  const qc = useQueryClient();
  const { t } = useT();
  const [note, setNote] = useState("");
  const isLogo = action === "new_logo";
  const m = useMutation({
    mutationFn: () => api.post(`/claims/${claim.id}/self-resolve`, { action, note: note.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(isLogo ? t("Claim filed under New logo — mailed by Back Office") : t("Claim resolved by Back Office"));
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="claim-self-resolve-dialog">
        <DialogHeader><DialogTitle>{isLogo ? "🏷 " + t("New logo sent") : "✓ " + t("Resolved")} · {claim.po_number}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
            {t("Closes the claim with your solution — no alert goes to Factory One or Super Admin.")}
          </div>
          <div>
            <Label className="text-xs">{t("Solution performed")} <span className="text-red-600">*</span></Label>
            <Textarea data-testid="claim-self-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1"
                      placeholder={isLogo ? t("e.g. mailed 2 new logos by Canada Post on Oct 2") : t("e.g. sent fitting instructions — customer confirmed it fits")} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={onClose} disabled={m.isPending} data-testid="claim-self-cancel">{t("Cancel")}</Button>
          <Button data-testid="claim-self-confirm" onClick={() => m.mutate()} disabled={m.isPending || !note.trim()}
                  className={isLogo ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}>
            {isLogo ? "🏷 " + t("New logo sent") : "✓ " + t("Resolved")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
